/**
 * Helpers neutros de upload usados tanto pelo wizard de peticionamento
 * quanto pelo upload genérico de artefatos. Centraliza:
 *
 * - `sha256OfBlob`: SHA-256 em hex lowercase de um Blob/File (SubtleCrypto).
 * - `uploadBlobToS3`: POST multipart de presigned URL via XMLHttpRequest,
 *   com suporte a progresso e AbortSignal (fetch não emite progress).
 *
 * Mantemos os nomes originais para preservar os call sites já existentes
 * em `lib/pwa/syncManager.ts`.
 */
/**
 * Forma mínima de uma presigned POST necessária para subir um Blob ao S3.
 * Ambos os endpoints (peticionamento e artefatos) emitem variantes desta
 * estrutura — usamos um tipo neutro para desacoplar do domínio específico.
 *
 * O wizard de peticionamento usa `url` (compatível com `PresignResponse`);
 * o init de artefato usa `upload_url`. Aceitamos os dois pela compatibilidade.
 */
export interface S3PresignedPost {
  /** URL alvo do POST multipart. */
  url?: string;
  /** Alias usado pelo init de artefato. */
  upload_url?: string;
  /** Campos do form gerados pelo backend (policy, signature, key, etc.). */
  fields: Record<string, string>;
}

/**
 * Computes the SHA-256 of a Blob/File using SubtleCrypto.
 * Returns the hash as lowercase hex.
 */
export async function sha256OfBlob(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Uploads a Blob to S3 via presigned POST.
 * Uses XMLHttpRequest because fetch has no native upload progress event.
 *
 * The "file" field must be LAST in the multipart body per the S3 contract.
 * We append presign fields before the file to respect that ordering.
 */
export async function uploadBlobToS3(
  presign: S3PresignedPost,
  blob: Blob,
  fileName: string,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    for (const [key, value] of Object.entries(presign.fields)) {
      form.append(key, value);
    }
    form.append('file', blob, fileName);

    const target = presign.url ?? presign.upload_url;
    if (!target) {
      reject(new Error('Presigned POST sem URL alvo (url/upload_url ausente)'));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', target);
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      });
    }
    if (signal) {
      if (signal.aborted) {
        reject(new Error('Upload cancelado'));
        return;
      }
      const onAbort = () => xhr.abort();
      signal.addEventListener('abort', onAbort, { once: true });
      const cleanup = () => signal.removeEventListener('abort', onAbort);
      xhr.addEventListener('loadend', cleanup, { once: true });
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Falha no upload ao S3 (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Falha de rede no upload ao S3'));
    xhr.onabort = () => reject(new Error('Upload cancelado'));
    xhr.send(form);
  });
}
